// App.js with React Router
import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import TidioChat from './components/TidioChat';
import CookieBanner from './components/CookieBanner';
import CookieSettingsModal from './components/CookieSettingsModal';
import { CookieConsentProvider, useCookieConsent } from './contexts/CookieConsentContext';
import { setHomepageVariant } from './utils/analytics';
import { isPrerendering } from './utils/prerendering';
import './App.css';

// Lazy-load alt homepage variant (original is eagerly loaded to match pre-rendered HTML)
const HomePageAlt = lazy(() => import('./pages/HomePageAlt'));

// Lazy-load non-homepage routes
const SignupPage = lazy(() => import('./pages/SignupPage'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const DocumentationPage = lazy(() => import('./pages/DocumentationPage'));
const EnterpriseDeploymentPage = lazy(() => import('./pages/EnterpriseDeploymentPage'));
const AppUnderTest = lazy(() => import('./pages/appundertest/AppUnderTest'));
const BlogListPage = lazy(() => import('./pages/BlogListPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const UseCaseIndexPage = lazy(() => import('./pages/UseCaseIndexPage'));
const UseCasePage = lazy(() => import('./pages/UseCasePage'));
const VideoPlayer = lazy(() => import('./pages/VideoPlayer'));

// A/B test: read the variant the server chose (stored in hp_variant cookie).
// This ensures React renders the same variant as the pre-rendered HTML the server sent.
function getHpVariant() {
  if (isPrerendering) {
    // During Puppeteer pre-render, use the URL to decide which variant to capture
    return window.location.pathname === '/alt' ? 'alt' : 'original';
  }
  const match = document.cookie.match(/(?:^|;\s*)hp_variant=([^;]*)/);
  const v = match ? match[1] : null;
  if (v === 'original' || v === 'alt') return v;
  // No cookie set (e.g. dev server or first visit) — randomly assign and persist
  const variant = Math.random() < 0.5 ? 'original' : 'alt';
  document.cookie = `hp_variant=${variant}; path=/; max-age=${365 * 24 * 60 * 60}; SameSite=Lax`;
  return variant;
}
const chosenVariant = getHpVariant();

const RandomHomePage = () => {
  useEffect(() => {
    setHomepageVariant(chosenVariant);
  }, []);

  return chosenVariant === 'alt' ? <HomePageAlt /> : <HomePage />;
};

const AppContent = () => {
  const location = useLocation();
  const { showSettings, closeSettings } = useCookieConsent();
  const isDocumentationPage = location.pathname === '/documentation';
  const isEnterpriseDeploymentPage = location.pathname === '/enterprise-deployment';
  const isAppUnderTest = location.pathname.startsWith('/appundertest');
  const isVideoPlayer = location.pathname === '/video';
  const hideChrome = isDocumentationPage || isEnterpriseDeploymentPage || isAppUnderTest || isVideoPlayer;

  // Scroll to hash anchor after page renders
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const tryScroll = (attempts = 0) => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        } else if (attempts < 10) {
          setTimeout(() => tryScroll(attempts + 1), 200);
        }
      };
      setTimeout(() => tryScroll(), 100);
    }
  }, [location]);

  return (
    <div className="App">
      {!hideChrome && <Navigation />}
      <main>
        <Suspense fallback={<div style={{ minHeight: '100vh' }} />}>
          <Routes>
            <Route path="/" element={<RandomHomePage />} />
            <Route path="/original" element={<HomePage />} />
            <Route path="/alt" element={<HomePageAlt />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/documentation" element={<DocumentationPage />} />
            <Route path="/enterprise-deployment" element={<EnterpriseDeploymentPage />} />
            <Route path="/blog" element={<BlogListPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/use-cases" element={<UseCaseIndexPage />} />
            <Route path="/use-cases/:slug" element={<UseCasePage />} />
            <Route path="/video" element={<VideoPlayer />} />
            <Route path="/appundertest/*" element={<AppUnderTest />} />
          </Routes>
        </Suspense>
      </main>
      {!hideChrome && <TidioChat />}
      {!hideChrome && <Footer />}
      {!hideChrome && <CookieBanner />}
      {showSettings && <CookieSettingsModal onClose={closeSettings} />}
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <CookieConsentProvider>
        <Router>
          <AppContent />
        </Router>
      </CookieConsentProvider>
    </HelmetProvider>
  );
}

export default App;
